import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";

import { BillingFacturationNetResponse } from "@/Billing/Api/FacturationNet/Dto/Responses/FacNetBillLightResponse";
import { BillingClaimStatusRequest } from "@/Billing/Api/PublicBilling/Dto/Request/BillingClaimStatusRequest";
import { billingClaimAction, fetchBillingClaimStatus, fetchEncounterById, getPrimaryClinicians, getWorkSafeBcFormById, loadPatient, loadWorkSafeBc, printBillingClaimReport, saveBillingClaimNote, saveBillingClaimStatusFilterSetting } from "@/Billing/Api/PublicBilling/Dto/Response/BillingClaimStatusClient";
import { BillingClaimStatus } from "@/Billing/Models/BillingClaimStatus";
import { BillingReconciliationNote } from "@/Billing/Models/BillingReconciliationNote";
import { BillingClaimStatusInboxModel } from "@/Billing/Models/UI/BillingClaimStatusInboxModel";
import { FacturationNetManager } from "@/Billing/Services/FacturationNet/FacturationNetManager";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { ExternalBillingClaimStatus } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimStatus";
import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { AddNotesPopupButton } from "@/Common/Components/Modals/AddNotesPopupButton";
import { NoteListItem } from "@/Common/Components/NotesList/NotesList";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { Guid } from "@/Common/Models/Guid";
import { BillingClaimStatusType, RedirectTo } from "@/Common/Models/UI/Enums";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { FacturationDTO } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { WorkSafeBcFormLight } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcFormLight";
import { WorkSafeBcFormValidator } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcFormValidator";
import { WorkSafeBcPhysicianReportModel } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcPhysicianReportModel";
import { WorkSafeBcWorkerModel } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcWorkerModel";
import { WorkSafeBcInvalidFieldsPopup } from "@/Patient/Components/MedicalNote/WorkSafeBc/SubComponent/WorkSafeBcInvalidFieldsPopup";
import { WorkSafeBcController } from "@/Patient/Components/MedicalNote/WorkSafeBc/WorkSafeBcController";
import { Encounter, EncounterRelatedEmployee } from "@/Patient/Models/MedicalSummary/Encounter";
import { BillingClaimStatusReportSettings } from "./Models/BillingClaimStatusReportSettings";
import { BillingClaimStatusSettings } from "./BillingClaimStatusSettings";

import styles from "./BillingClaimStatusInbox.module.scss";

interface BillingClaimStatusInboxProp {
    model: BillingClaimStatusInboxModel;
    onActionCallback: () => void;
}

function BillingClaimStatusInbox(props: BillingClaimStatusInboxProp): JSX.Element {
    const { getText } = useTranslation();
    const saveOptionKey: string = "BillingClaimStatusInbox";
    const tableColumnNames = {
        Checkbox: "Checkbox",
        AppointmentStartDateTime: "AppointmentStartDateTime",
        Duration: "Duration",
        Service: "Service",
        PatientContactId: "PatientContactId",
        Provider: "Provider",
        ReferralProvider: "ReferralProvider",
        PatientNAM: "PatientNAM",
        VulnerableCode: "VulnerableCode",
        LastPayment: "LastPayment",
        InvoiceStatus: "InvoiceStatus",
        Action: "Action"
    };

    const currentEmployee = sessionStore.getCurrentEmployee();

    const [billingClaimStatusInboxData, setBillingClaimStatusInboxData] = useState([]);
    const [filterSettingsVisibility, setFilterSettingsVisibility] = useState<boolean>(false);
    const [isColumnOrderingPopupVisible, setIsColumnOrderingPopupVisible] = useState<boolean>(false);

    const [isSelectAllChecked, setSelectAllCheckboxes] = useState(false);
    const [tableTotalRows, setTableTotalRows] = useState(0);
    const [billingClaimStatusReportSettings, setBillingClaimStatusReportSettings] = useState(new BillingClaimStatusReportSettings(1));
    const [worksafeBcFormValidate, setWorksafeBcFormValidate] = useState(true);
    const [currentEncounterId, setCurrentEncounterId] = useState(null);
    const [searchText, setSearchText] = useState<string>(null);
    const [ignoreLabelVisibility, setIgnoreLabelVisibility] = useState<boolean>(false);

    const getMyleTableColumnsDefaultOptions = (): MyleTableColumnOptions[] => [
        new MyleTableColumnOptions({
            ...getCommonTableColumnOptions("", tableColumnNames.Checkbox, null, 0, false),
            IsFixedInPlace: true,
            RowClickEnabled: false,
            IsSortable: false,
            FloatLeft: true,
            WidthPixels: 38
        }),
        new MyleTableColumnOptions({
            ...getCommonTableColumnOptions(getText("BillingReconciliation_AppointmentDateLabel"), tableColumnNames.AppointmentStartDateTime, BillingClaimStatusType.AppointmentDate, 12),
            IsFixedInPlace: true
        }),
        getCommonTableColumnOptions(getText("ServiceLabel"), tableColumnNames.Service, null, 12, false),
        getCommonTableColumnOptions(getText("PatientLabel"), tableColumnNames.PatientContactId, null, 12, false),
        getCommonTableColumnOptions(getText("HealthCard"), tableColumnNames.PatientNAM, null, 10, false),
        getCommonTableColumnOptions(getText("BillingReconciliation_VulnerableCodeLabel"), tableColumnNames.VulnerableCode, null, 5, false),
        getCommonTableColumnOptions(getText("ProviderLabel"), tableColumnNames.Provider, null, 10, false),
        getCommonTableColumnOptions(getText("ReferralLabel"), tableColumnNames.ReferralProvider, null, 10, false),
        new MyleTableColumnOptions({
            ...getCommonTableColumnOptions(getText("BillingReconciliation_InvoiceStatusLabel"), tableColumnNames.InvoiceStatus, null, 10, false),
            DataCy: "public-billing-invoice"
        }),
        getCommonTableColumnOptions(getText("BillingReconciliation_LastPaymentRequestLabel"), tableColumnNames.LastPayment, null, 10, false),
        new MyleTableColumnOptions({
            ...getCommonTableColumnOptions(getText("ActionLabel"), tableColumnNames.Action, null, 6, false),
            IsFixedInPlace: true,
            DataCy: "public-billing-action"
        })
    ];

    function getCommonTableColumnOptions(
        headerText: string,
        fieldName: string,
        sortColumn: BillingClaimStatusType,
        WidthPercentage: number,
        isSortable: boolean = true
    ): MyleTableColumnOptions {
        return new MyleTableColumnOptions({
            HeaderText: headerText,
            FieldName: fieldName,
            RowClickEnabled: true,
            IsSortable: isSortable,
            SortColumn: sortColumn,
            SortDirection: billingClaimStatusReportSettings.sortDirection,
            IsSortActive: billingClaimStatusReportSettings.claimStatusType === sortColumn,
            WidthPercentageDefault: WidthPercentage,
            sortingMethod: () => tableSort(sortColumn)
        });
    }

    function tableSort(billingClaimType: BillingClaimStatusType): void {
        const sortDirection = MyleTableHelper.getNewSortDir(billingClaimType, billingClaimStatusReportSettings.claimStatusType, billingClaimStatusReportSettings.sortDirection);
        if (billingClaimStatusReportSettings.claimStatusType !== billingClaimType) {
            setBillingClaimStatusReportSettings({ ...billingClaimStatusReportSettings, claimStatusType: billingClaimType });
        }
        if (billingClaimStatusReportSettings.sortDirection !== sortDirection) {
            setBillingClaimStatusReportSettings({ ...billingClaimStatusReportSettings, sortDirection: sortDirection });
        }
    }

    function loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 50,
            MinTableWidth: 950,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };
        return Immutable.fromJS(defaultTablePluginOptions);
    }

    function getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return columnsOptions
            .map(columnOption => buildTableColumn(columnOption))
            .toList();
    }

    function buildTableColumn(option: MyleTableColumnOptions): MyleTableColumn {
        const column = new MyleTableColumn(option);
        column.renderCell = (item: TableDataSourceItem<BillingClaimStatus>) => renderTextCell(option.FieldName, item);
        if (option.FieldName === tableColumnNames.Checkbox)
            column.renderHeader = () => renderCheckboxHeader();

        return column;
    }

    function setSelectAllItems(checked: boolean): void {
        setSelectAllCheckboxes(checked);
        setIgnoreLabelVisibility(checked);
        setBillingClaimStatusInboxData(billingClaimStatusInboxData.map(bill => ({ ...bill, IsSelected: checked })));
    }

    function setSelectIndividualItem(item: TableDataSourceItem<BillingClaimStatus>): void {
        const updatedBillingClaimStatusData = billingClaimStatusInboxData.map(bill => {
            if (item.Model.AppointmentId && bill.Model.AppointmentId === item.Model.AppointmentId) {
                return { ...bill, IsSelected: !item.IsSelected };
            }
            else {
                return bill;
            }
        });

        setIgnoreLabelVisibility(updatedBillingClaimStatusData.any(bill => bill.IsSelected));
        setBillingClaimStatusInboxData(updatedBillingClaimStatusData);
        setSelectAllCheckboxes(updatedBillingClaimStatusData.every(bill => bill.IsSelected));
    }

    function renderTextCell(fieldName: string, item: TableDataSourceItem<BillingClaimStatus>): JSX.Element {
        return (
            <div className={`${item.Model.IsNoNote || item.Model.IsNoPaymentRequest ? "bold" : ""}`}>
                <span className="text-wrap-control">{formatCellText(fieldName, item)}</span>
            </div>
        );
    }

    function formatCellText(fieldName: string, item: TableDataSourceItem<BillingClaimStatus>): string | JSX.Element {
        switch (fieldName) {
            case tableColumnNames.Checkbox:
                return renderCheckboxCell(item);
            case tableColumnNames.PatientContactId:
                return renderPatient(item.Model);
            case tableColumnNames.Provider:
                return getFormatedProviders(item.Model.Providers);
            case tableColumnNames.ReferralProvider:
                const referralProvider = (item.Model.ReferralLastName && item.Model.ReferralFirstName) ?
                    `${item.Model.ReferralLastName}, ${item.Model.ReferralFirstName}` : (item.Model.CustomReferralName) ? item.Model.CustomReferralName : "";
                return (
                    <div className={styles.leftPadding1} title={referralProvider}>
                        {referralProvider}
                    </div>
                );
            case tableColumnNames.AppointmentStartDateTime:
                return getFormatAppointment(item.Model);
            case tableColumnNames.Service:
                const service = cacheStore.GetAppointmentServiceDescription(item.Model.ServiceDefinitionId?.toString(), item.Model.ComboId);
                return (
                    <div className={styles.leftPadding} title={service}>
                        {service}
                    </div>
                );
            case tableColumnNames.InvoiceStatus:
                return renderInvoiceStatus(item.Model);
            case tableColumnNames.VulnerableCode:
                return renderVulnerableCodes(item.Model);
            case tableColumnNames.LastPayment:
                return renderLastPaymentRequest(item.Model);
            case tableColumnNames.Action:
                return renderAction(item.Model);
            default:
                return (
                    <div title={item.Model[fieldName]} className={styles.leftPadding}>
                        {item.Model[fieldName]}
                    </div>
                );
        }
    }

    function renderCheckboxHeader(): JSX.Element {
        return (
            <div>
                <CheckboxControl
                    checked={isSelectAllChecked}
                    onChange={checked => setSelectAllItems(checked)}
                />
            </div>
        );
    }

    function renderCheckboxCell(data: TableDataSourceItem<BillingClaimStatus>): JSX.Element {
        return (
            <div>
                <CheckboxControl
                    id={data.Model.AppointmentId?.toString()}
                    checked={data.IsSelected}
                    onChange={() => setSelectIndividualItem(data)}
                />
            </div>
        );
    }

    function renderPatient(data: BillingClaimStatus): JSX.Element {
        return (
            <div className={styles.leftPadding} title={`${data.PatientLastName}, ${data.PatientFirstName}`}>
                <HyperLinkRedirectControl
                    className="text-uppercase"
                    target={RedirectTo.Patient}
                    redirectValue={data.PatientContactId}
                    text={`${data.PatientLastName}, ${data.PatientFirstName}`}
                />
            </div>
        );
    }

    function renderNoNoteStatus(data: BillingClaimStatus): JSX.Element {
        return (
            <HyperLinkRedirectControl
                target={RedirectTo.Patient}
                redirectValue={data.PatientContactId}
                text={getText("BillingReconciliation_NoNoteLabel")}
            />
        );
    }

    function renderInvoiceStatus(data: BillingClaimStatus): JSX.Element {
        return (
            <div className={styles.leftPadding1}>
                {data.IsIgnored ? <div>{getText("BillingReconciliation_IgnoredLabel")}</div> : renderNotIgnoredInvoiceStatus(data)}
            </div>
        );
    }

    function renderNoPaymentStatus(note: Encounter, patientContactd: Guid, siteId: Guid): JSX.Element {
        return (
            <div>
                <a id={`noPaymentrequest_${note.Id}`} onClick={() => createNewClaimAsync(note, patientContactd, siteId)}>{getText("BillingReconciliation_NoPaymentRequestLabel")}</a>
            </div>
        );
    }

    function isClinicAidStagingClaim(claimStatus: ExternalBillingClaimStatus, claimBillingProvider: BillingProvider): boolean {
        return (claimStatus === ExternalBillingClaimStatus.New)
            && (cacheStore.ClinicSettings.EnableClinicAidCreateClaimFlow)
            && (claimBillingProvider == BillingProvider.ClinicAid);
    }

    function renderNotIgnoredInvoiceStatus(data: BillingClaimStatus): JSX.Element {
        return (
            <>
                {data.IsNoNote ?
                    renderNoNoteStatus(data) :
                    (data.Notes.map(note => (
                        (data.MedfarBillingList.any(medfar => medfar.EncounterId === note.Id)) ||
                            (data.FacturationNetInvoices.any(facturation => facturation.EncounterId === note.Id)) ||
                            (!isArrayNullOrUndefinedOrEmpty(data.ExternalBillingClaims)) && data.ExternalBillingClaims.any(external => external.EncounterId === note.Id && !isClinicAidStagingClaim(external.Status, external.BillingProvider)) ?
                            (
                                <div>
                                    {data.MedfarBillingList.any(medfar => medfar.EncounterId === note.Id) && (
                                        renderMedfarBillList(data.MedfarBillingList.filter(medfar => medfar.EncounterId === note.Id), data.PatientContactId)
                                    )}
                                    {data.FacturationNetInvoices.any(facturation => facturation.EncounterId === note.Id) && (
                                        renderFacturationNetBillsList(data.FacturationNetInvoices.filter(facturation => facturation.EncounterId === note.Id))
                                    )}
                                    {data.ExternalBillingClaims.any(external => external.EncounterId === note.Id) && (
                                        renderExternalBillsList(data.ExternalBillingClaims.filter(external => external.EncounterId === note.Id && !isClinicAidStagingClaim(external.Status, external.BillingProvider)))
                                    )}
                                </div>
                            ) :
                            renderNoPaymentStatus(note, data.PatientContactId, data.SiteId)
                    )))

                }
            </>
        );
    }

    function renderMedfarBillList(bills: FacturationDTO[], patientContactId: Guid): JSX.Element[] {
        return (bills ?? [])
            .map(bill => (
                <div key={`medfar_${bill.FacturationRequestID}`}>
                    <a onClick={() => openMedicalNoteForMedfarClaim(patientContactId, bill.EncounterId)}>
                        {bill.FacturationCodes?.map(fac => fac.CodeFacturation).join(", ")}
                    </a>
                </div>
            ));
    }

    function renderFacturationNetBillsList(bills: BillingFacturationNetResponse[]): JSX.Element[] {
        return (bills ?? [])
            .map(bill => (
                <div key={`facNet_${bill.ClaimId}`}>
                    {currentEmployee.ContactFacetId === bill.CreatedByEmployeeId && (
                        <a onClick={() => FacturationNetManager.openBill(bill.ClaimId, tableRefreshAfterClaimWindowClose)}>
                            {BillingFacturationNetResponse.getBillDisplayString(bill)}
                        </a>
                    )}
                    {currentEmployee.ContactFacetId !== bill.CreatedByEmployeeId && (
                        <span>
                            {BillingFacturationNetResponse.getBillDisplayString(bill)}
                        </span>
                    )}
                </div>
            ));
    }

    function renderExternalBillsList(claims: ExternalBillingClaimDto[]): JSX.Element[] {
        return (claims ?? [])
            .map(claim => {
                const canViewClaim = ExternalBillingManager.canEmployeeViewClaimDetails(currentEmployee, claim);

                return (
                    <div key={`external_${claim.BillingProvider}_${claim.ProviderClaimId}`}>
                        {canViewClaim && (
                            <a
                                onClick={async () => await ExternalBillingManager.openClaimWindowAsync(claim, tableRefreshAfterClaimWindowClose)}
                            >
                                {ExternalBillingManager.getDisplayText(claim)}
                            </a>
                        )}
                        {!canViewClaim && (
                            <span>
                                {ExternalBillingManager.getDisplayText(claim)}
                            </span>
                        )}
                    </div>
                );
            });
    }

    function renderVulnerableCodes(data: BillingClaimStatus): JSX.Element {
        return (
            <>
                {data.MedfarBillingList.map(bill => `${bill.VulnerabilityCode1 ?? ""} ${bill.VulnerabilityCode2 ?? ""} ${bill.VulnerabilityCode3 ?? ""}`)}
            </>
        );
    }

    function renderLastPaymentRequest(data: BillingClaimStatus): JSX.Element {
        return (
            <div className={styles.leftPadding1}>
                {data.MedfarBillingList?.any() ? data.MedfarBillingList.map((bill, index) => <div key={`medfarbill_${index}`}>{formatDateOnly(bill.CreationDate)}</div>) : ""}
                {data.FacturationNetInvoices?.any() ? data.FacturationNetInvoices.map((bill, index) => <div key={`facnet_${index}`}>{formatDateOnly(bill.CreationDate)}</div>) : ""}
                {data.ExternalBillingClaims?.any() ? data.ExternalBillingClaims.map((bill, index) => bill.Status === ExternalBillingClaimStatus.New ? "" : <div key={`external_${index}`}>{formatDateOnly(bill.DateCreated)}</div>) : ""}
            </div>
        );
    }

    function renderAction(data: BillingClaimStatus): JSX.Element {
        return (
            <div className={styles.leftPadding1}>
                {renderNotes(data)}
                <span className={styles.leftRightPadding}>/</span>
                <a
                    aria-expanded="false"
                    onClick={() => ignoreOrRestoreBillingClaimItem(data)}
                >
                    {!data.IsIgnored ? getText("IgnoreLabel") : getText("RestoreLabel")}
                </a>
            </div>
        );
    }

    function renderNotes(data: BillingClaimStatus): JSX.Element {
        return (
            <AddNotesPopupButton
                notes={mapBillingNotesToNotes(data.BillingNotes)}
                onSubmit={async note => await addBillingClaimNote(data.AppointmentId, note)}
                itemId={data.AppointmentId}
            />
        );
    }

    function getFormatAppointment(data: BillingClaimStatus): JSX.Element {
        return (
            <div>{data.AppointmentStartDateTime ? (
                <>
                    <div title={formatDate(data.AppointmentStartDateTime)}>
                        {formatDate(data.AppointmentStartDateTime)}
                    </div>
                    <div title={`${getText("DurationLabel")}: ${(data.Duration / 600000000)} min`}>
                        {`${getText("DurationLabel")}: ${(data.Duration / 600000000)} min`}
                    </div>
                </>
            ) : ("--")
            }
            </div>
        );
    }

    function formatDate(date: Date): string {
        return LuxonDateTimeParser
            .parse(date)
            ?.toLocal()
            ?.toFormat(`yyyy/LL/dd '${getText("AtLabel")}' HH:mm:ss`) ?? "";
    }

    function formatDateOnly(date: Date): string {
        return LuxonDateTimeParser
            .parse(date)
            ?.toLocal()
            ?.toFormat("yyyy/LL/dd") ?? "";
    }

    function getFormatedProviders(providers: IndividualLight[]): JSX.Element {
        return (
            <div className={styles.leftPadding}>
                {providers?.map(
                    provider => (provider) ? (
                        <div key={`prov_${provider.Id}`} title={`${provider.Prefix ? cacheStore.GetLocalizedValueByLookupId(provider.Prefix) : ""} ${provider.LastName} ${provider.FirstName}`}>
                            {`${provider.Prefix ? cacheStore.GetLocalizedValueByLookupId(provider.Prefix) : ""} ${provider.LastName} ${provider.FirstName}`}
                        </div>
                    ) : ""
                )}
            </div>
        );
    }

    const updateBillingClaimStatusData = useCallback(
        debounce(
            async (billingClaimStatusRequestData: BillingClaimStatusRequest): Promise<void> => {
                const responseData = await fetchBillingClaimStatus(billingClaimStatusRequestData);
                setTableTotalRows(responseData.QueryRowCount);
                responseData.BillingClaimStatusResults.map(
                    bill => bill.Providers = responseData?.ProvidersCached.filter(provider => bill.ProviderIds.contains(provider.Id))
                );

                responseData.BillingClaimStatusResults?.filter(p => p.Notes.any()).forEach(data => {
                    data.Notes.forEach(note => {
                        note.SiteId = data.SiteId;
                        const encounterEmployees = responseData.EncounterEmployees?.filter(p => p.EncounterId === note.Id);
                        const encounterEmployee = encounterEmployees?.firstOrDefault();
                        if (encounterEmployee) {
                            note.EncounterRelatedEmployees = note.EncounterRelatedEmployees ?? [];
                            encounterEmployee.EmployeeIds.forEach(employeeId => {
                                const encounterRelatedEmployee = new EncounterRelatedEmployee();
                                encounterRelatedEmployee.EncounterId = encounterEmployee.EncounterId;
                                encounterRelatedEmployee.EmployeeId = employeeId;
                                note.EncounterRelatedEmployees?.push(encounterRelatedEmployee);
                            });
                        }
                    });
                });

                setBillingClaimStatusInboxData(responseData.BillingClaimStatusResults?.map(item => new TableDataSourceItem<BillingClaimStatus>(item)));

                const updatedStatus = await fetchBillingClaimStatusFromBillingProvider(responseData.BillingClaimStatusResults);
                setBillingClaimStatusInboxData(updatedStatus.map(item => new TableDataSourceItem<BillingClaimStatus>(item)));
            },
            400
        ),
        []
    );

    async function fetchBillingClaimStatusFromBillingProvider(billingClaimStatus: BillingClaimStatus[]): Promise<BillingClaimStatus[]> {
        if (isArrayNullOrUndefinedOrEmpty(billingClaimStatus))
            return [];

        const billingClaimStatusEncounterIds = billingClaimStatus.reduce((acc, item) => {
            const ids = item.ExternalBillingClaims.map(claim => claim.EncounterId);
            ids.forEach(id => acc.add(id));
            return acc;
        }, new Set<string>());

        if (billingClaimStatusEncounterIds.size === 0)
            return billingClaimStatus;

        // fetch updated invoices
        const updatedExternalBillingClaims = await ExternalBillingManager.getClaimsByEncounterIdsAsync(Array.from(billingClaimStatusEncounterIds).map(String), true);

        let temp = [];
        for (let billingClaim of billingClaimStatus) {
            let copied = { ...billingClaim };
            copied.ExternalBillingClaims = filterExternalBillingClaims(updatedExternalBillingClaims, billingClaim.ExternalBillingClaims);

            temp.push(copied);
        }

        return temp;
    }

    function filterExternalBillingClaims(
        updatedExternalClaims: ExternalBillingClaimDto[],
        currentExternalClaims: ExternalBillingClaimDto[]
    ): ExternalBillingClaimDto[] {
        if (isArrayNullOrUndefinedOrEmpty(updatedExternalClaims))
            return updatedExternalClaims;

        const currentClaimsSet = new Set((currentExternalClaims ?? []).map(x => x.EncounterId));

        return updatedExternalClaims.filter(updatedClaim => currentClaimsSet.has(updatedClaim.EncounterId));
    }

    let columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, getMyleTableColumnsDefaultOptions());
    const tableRenderOptions = loadTableRenderOptions(columnsOptions);
    const columnOptionsForOrderColumnsPopup = new MyleHelper().castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

    const loadBillingClaimStatus = useCallback(async (filterSetting: BillingClaimStatusInboxModel, searchText?: string): Promise<void> => {
        const billingClaimStatusRequest: BillingClaimStatusRequest = {
            CurrentPageNumber: billingClaimStatusReportSettings.currentPageNumber,
            SearchText: searchText,
            QuerySortDirection: billingClaimStatusReportSettings.sortDirection,
            IsPrint: false,
            BillingClaimStatusFilterSettings: BillingClaimStatusInboxModel.toCacheModel(filterSetting)
        };
        await updateBillingClaimStatusData(billingClaimStatusRequest);

        if (filterSetting.followCurrentTime) {
            sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_ToDateChanged, { value: new Date(), followCurrentTime: true });
        }
    }, [billingClaimStatusReportSettings, updateBillingClaimStatusData]);

    async function ignoreBillingClaimItems(): Promise<void> {
        setIgnoreLabelVisibility(false);
        const selectedAppointments = billingClaimStatusInboxData.filter(bill => bill.IsSelected).map(billItem => billItem.Model.AppointmentId);
        const ignoredAppointments = selectedAppointments.map(appointment => ({ AppointmentId: appointment, IsIgnored: true }));
        const requestData = {
            BillingReconciliationActionItems: ignoredAppointments
        };
        const response = await billingClaimAction(requestData);
        if (response.IsSuccess) {
            loadBillingClaimStatus(props.model, searchText);
            props.onActionCallback();
        }
    }

    async function ignoreOrRestoreBillingClaimItem(data: BillingClaimStatus): Promise<void> {
        const ignoredOrRestoredAppointment = [{ AppointmentId: data.AppointmentId, IsIgnored: !data.IsIgnored }];
        const requestData = {
            BillingReconciliationActionItems: ignoredOrRestoredAppointment
        };
        const response = await billingClaimAction(requestData);
        if (response.IsSuccess) {
            loadBillingClaimStatus(props.model, searchText);
            props.onActionCallback();
        }
    }

    useEffect(() => {
        loadBillingClaimStatus(props.model, searchText);
        $(window).trigger("resize");
    }, [loadBillingClaimStatus]);

    function toggleFilterSetting(): void {
        setFilterSettingsVisibility(!filterSettingsVisibility);
    }

    function settingSaveChanges(isSaveSettingsChecked: boolean): void {
        loadBillingClaimStatus(props.model, searchText);

        if (isSaveSettingsChecked) {
            saveBillingClaimStatusFilterSetting(props.model);
        }
        setBillingClaimStatusReportSettings({ ...billingClaimStatusReportSettings, currentPageNumber: 1 });
        toggleFilterSetting();
    }

    const billingProvider = useMemo(
        () => sessionStore.getCurrentEmployee().getBillingProviderResolved(),
        []
    );

    async function createNewClaimAsync(encounter: Encounter, patientContactId: Guid, siteId: Guid): Promise<void> {
        if (BillingProviderHelper.shouldUseLegacyMedfarBilling(billingProvider)) {
            openMedicalNoteForMedfarClaim(patientContactId, encounter.Id);
            return;
        }

        if (BillingProviderHelper.shouldUseLegacyFacturationNetBilling(billingProvider)) {
            await createNewFacturationNetClaim(encounter.Id, encounter.PatientId);
            return;
        }

        if (isWorkSafeBcFormEnabledForBilling(siteId)) {
            const formIsValid = await loadAndValidateWorkSafeBcFormForClaim(encounter.Id, patientContactId);

            setWorksafeBcFormValidate(formIsValid);

            if (!formIsValid) {
                setCurrentEncounterId(encounter.Id);
                return;
            }
        }

        await openCreateEncounterClaimWindowAsync(encounter);
    }

    async function openCreateEncounterClaimWindowAsync(encounter: Encounter): Promise<void> {
        let primaryClinicians: string[] = null;
        if (cacheStore.ClinicSettings.EnableExternalBillingEmployeeSelectPopup && BillingProviderHelper.getBillingProviderSupportsEmployeeSelect(billingProvider))
            primaryClinicians = await getIntervenors(encounter.PatientId);

        await ExternalBillingManager.openCreateEncounterClaimWindowAsync(
            encounter,
            billingProvider,
            await sessionStore.getCurrentEmployee().getExternalBillingUserAsync(),
            billingRefreshAfterClaimCreate,
            null,
            primaryClinicians
        );
    }

    async function getIntervenors(patientId: string): Promise<string[]> {
        const response = await getPrimaryClinicians(patientId);

        return response.patientRefIntervenorWithDetails
            .filter(interv => interv.Intervenor.IsPrimary)
            .map(interv => interv.Intervenor.EmployeeId);
    }

    async function openCreateWorkSafeBcEncounterClaimWindowAsync(encounter: Encounter): Promise<void> {
        await openCreateEncounterClaimWindowAsync(encounter);

        setWorksafeBcFormValidate(true);
    }

    async function openMedicalNoteForMedfarClaim(patientContactId: Guid, encounterId: string): Promise<void> {
        sessionStore.dispatch(
            LocalOperations.Misc.RedirectToTab,
            {
                value: patientContactId,
                target: RedirectTo.Patient,
                currentLocation: RouterHistory.location,
                OpenEncounterId: encounterId,
                encounterEdit: false
            }
        );
    }

    async function createNewFacturationNetClaim(encounterId: string, patientId: string): Promise<void> {
        const response = await fetchEncounterById({ EncounterId: encounterId });
        const currentEncounter = response.Encounter;
        const createBill = (): void => FacturationNetManager.createBill(currentEncounter, patientId, billingRefreshAfterClaimCreate);

        if (sessionStore.isScheduledVisitChecked == null)
            sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.ShowVisitTypeSelectionPopup, { callback: createBill });
        else
            createBill();
    }

    async function loadAndValidateWorkSafeBcFormForClaim(encounterId: string, patientContactId: Guid): Promise<boolean> {
        const response = await loadWorkSafeBc({
            PatientContactId: patientContactId,
            EncounterId: encounterId
        });

        const currentFormLight: WorkSafeBcFormLight = response?.Forms?.firstOrDefault(form => form.EncounterId === encounterId);

        // return valid if there is no form
        // You can't have an invalid form if you don't have a form 👉😏
        if (!currentFormLight)
            return true;

        const workSafeBcController = React.createRef<WorkSafeBcController>();
        await workSafeBcController?.current?.saveForm();
        const currentForm = await getWorkSafeBcForm(currentFormLight.Id);

        if (!currentForm)
            return true;

        const patient = await loadPatient({
            IndividualId: patientContactId,
            IncludeRelatedRecalls: false
        });

        const workerModel = WorkSafeBcWorkerModel.fromPatient(patient);
        const validator = new WorkSafeBcFormValidator(currentForm, workerModel);

        return validator.isFormValidForClaim();
    }

    function isWorkSafeBcFormEnabledForBilling(siteId: Guid): boolean {
        return BillingProviderHelper.getBillingProviderSupportsWorkSafeBcForms(billingProvider)
            && isWorkSafeBcFormEnabled(siteId);
    }

    function isWorkSafeBcFormEnabled(siteId: Guid): boolean {
        const encounterSite = cacheStore.GetSiteById(siteId?.toString());

        return cacheStore.ClinicSettings.EnableWorkSafeBcFormFeature
            && encounterSite.isSiteInProvince(CanadaState.BritishColumbia);
    }

    async function getWorkSafeBcForm(formId: string): Promise<WorkSafeBcPhysicianReportModel> {
        const response = await getWorkSafeBcFormById(formId);

        return new WorkSafeBcPhysicianReportModel(response.PhysicianReport);
    }

    function tableRefresh(): void {
        loadBillingClaimStatus(props.model, searchText);
    }

    function tableRefreshAfterClaimWindowClose(): void {
        setTimeout(
            () => {
                loadBillingClaimStatus(props.model, searchText);
            },
            1000
        );
    }

    function billingRefreshAfterClaimCreate(): void {
        tableRefreshAfterClaimWindowClose();
        props.onActionCallback();
    }

    function searchBillingClaimStatus(searchText: string): void {
        setSearchText(searchText);
        loadBillingClaimStatus(props.model, searchText);
    }

    async function printBillingReconciliation(): Promise<void> {
        const columnConfig = cacheStore.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());
        await printBillingClaimReport(props.model, columnConfig, searchText);
    }

    async function addBillingClaimNote(appointmentId: Guid, note: string): Promise<boolean> {
        const requestdata = { AppointmentId: appointmentId, Note: note };
        const response = await saveBillingClaimNote(requestdata);
        setBillingClaimStatusInboxData(billingClaimStatusInboxData.map(bill => {
            if (bill.Model.AppointmentId === response.BillingReconciliationNote.AppointmentId)
                bill.Model.BillingNotes.unshift(response.BillingReconciliationNote);
            return bill;
        }));
        return !!response;
    }

    /**
    * Map billing reconciliation notes to generic models for the notes popup.
    * @param notes The billing reconciliation notes to map.
    */
    function mapBillingNotesToNotes(notes: BillingReconciliationNote[]): NoteListItem[] {
        return notes.map(note => {
            const employee = cacheStore.GetEmployeeByContactFacetId(note.CreatedByEmployeeId);

            return {
                id: `${note.CreatedDate}`,
                content: note.Note,
                user: Employee.getFullNameWithPrefixContactFacetForEmployee(employee),
                date: new Date(note.CreatedDate)
            };
        });
    }

    return (
        <div className="workspace">
            <CommonTableHeaderComponent
                title={getText("BillingReconciliation_PaymentRequestStatusAppointmentLabel")}
                searchText={searchText}
                searchPlaceholder={getText("SearchLabel")}
                onRefreshClick={() => tableRefresh()}
                displaySearchIcon={true}
                onSettingsClick={() => toggleFilterSetting()}
                onPrintClick={() => printBillingReconciliation()}
                onSearch={text => searchBillingClaimStatus(text)}
            />
            {filterSettingsVisibility && (
                <BillingClaimStatusSettings
                    model={props.model}
                    onSaveChanges={isSaveSettingsChecked => settingSaveChanges(isSaveSettingsChecked)}
                    onClose={() => toggleFilterSetting()}
                    onColumnOrderingPopupOpenClick={() => {
                        setIsColumnOrderingPopupVisible(!isColumnOrderingPopupVisible);
                        toggleFilterSetting();
                    }}
                />
            )}

            {isColumnOrderingPopupVisible && (
                <MyleTableColumnOrderControlv2
                    TableColumns={columnOptionsForOrderColumnsPopup}
                    SaveOptionskeyTarget={saveOptionKey}
                    ContainerId="BillingClaimStausColumnSettingsPopup"
                    closeColumnOrderPopup={() => setIsColumnOrderingPopupVisible(!isColumnOrderingPopupVisible)}
                />
            )}
            <div id="posref" className="ws-scrollable">
                <div className="myle-doc-tb main-content myle-form">
                    <MyleTable
                        TableRenderOptions={tableRenderOptions}
                        TableId="billingClaim_inbox_table"
                        Columns={getTableColumns(columnsOptions)}
                        DataSource={billingClaimStatusInboxData?.toImmutable()}
                        SaveOptionskeyTarget={saveOptionKey}
                        TableDataCy="billingInbox-table"
                    />
                </div>
            </div>
            <div className="ws-footer table-pager-footer">
                <div className="row">
                    <div className="col-lg-10 col-md-9 col-sm-8">
                        <SimplePagerControl
                            PageSize={props.model.queryPageSize}
                            TotalRows={tableTotalRows}
                            CurrentPage={billingClaimStatusReportSettings.currentPageNumber}
                            changePageFunction={currentPageNumber => setBillingClaimStatusReportSettings(prevState => ({ ...prevState, currentPageNumber: currentPageNumber }))}
                        />
                    </div>
                    <div className="col-lg-2 col-md-3 col-sm-4 text-right">
                        <button
                            className="btn btn-primary push-right-5"
                            onClick={() => ignoreBillingClaimItems()}
                            disabled={!ignoreLabelVisibility}
                            data-cy="billingClaimStatus-ignore"
                        >
                            {getText("IgnoreLabel")}
                        </button>
                    </div>
                </div>
            </div>

            {isWorkSafeBcFormEnabled && !worksafeBcFormValidate && (
                <WorkSafeBcInvalidFieldsPopup
                    onCreateClaim={() => openCreateWorkSafeBcEncounterClaimWindowAsync(currentEncounterId)}
                    onClose={() => setWorksafeBcFormValidate(true)}
                />
            )}
        </div>
    );
}

export { BillingClaimStatusInbox };
